import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import { api } from '../services/api';

/**
 * Query keys for bookmark-related queries
 */
export const bookmarkKeys = {
  all: ['bookmarks'] as const,
  lists: () => [...bookmarkKeys.all, 'list'] as const,
  list: (page: number, pageSize: number) =>
    [...bookmarkKeys.lists(), { page, pageSize }] as const,
  infinite: () => [...bookmarkKeys.all, 'infinite'] as const,
};

/**
 * Hook to create a bookmark
 * Automatically invalidates relevant queries on success
 */
export function useCreateBookmark() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (activityId: number) => api.createBookmark(activityId),
    onSuccess: () => {
      // Invalidate bookmarks list to refetch
      queryClient.invalidateQueries({ queryKey: bookmarkKeys.lists() });
      queryClient.invalidateQueries({ queryKey: bookmarkKeys.infinite() });
    },
  });
}

/**
 * Hook to fetch bookmarked activities with pagination
 */
export function useBookmarkedActivities(page: number = 1, pageSize: number = 20) {
  return useQuery({
    queryKey: bookmarkKeys.list(page, pageSize),
    queryFn: () => api.getBookmarkedActivities(page, pageSize),
    // Keep previous data while fetching next page for smooth UX
    placeholderData: (previousData) => previousData,
  });
}

/**
 * Hook to fetch bookmarked activities with infinite scroll
 * Automatically handles pagination
 */
export function useInfiniteBookmarks(pageSize: number = 20) {
  return useInfiniteQuery({
    queryKey: bookmarkKeys.infinite(),
    queryFn: ({ pageParam = 1 }) => api.getBookmarkedActivities(pageParam, pageSize),
    getNextPageParam: (lastPage) => {
      return lastPage.pagination.hasNextPage
        ? lastPage.pagination.page + 1
        : undefined;
    },
    initialPageParam: 1,
  });
}

/**
 * Hook to delete a bookmark
 * Supports optimistic updates
 */
export function useDeleteBookmark() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (bookmarkId: number) => api.deleteBookmark(bookmarkId),
    
    // Optimistic update - remove from UI immediately
    onMutate: async (bookmarkId) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: bookmarkKeys.infinite() });

      // Snapshot previous value
      const previousData = queryClient.getQueryData(bookmarkKeys.infinite());

      // Optimistically update cache
      queryClient.setQueryData(bookmarkKeys.infinite(), (old: any) => {
        if (!old) return old;

        return {
          ...old,
          pages: old.pages.map((page: any) => ({
            ...page,
            bookmarks: page.bookmarks.filter((b: any) => b.id !== bookmarkId),
            pagination: {
              ...page.pagination,
              totalCount: page.pagination.totalCount - 1,
            },
          })),
        };
      });

      return { previousData };
    },

    // Rollback on error
    onError: (_err, _bookmarkId, context: any) => {
      if (context?.previousData) {
        queryClient.setQueryData(bookmarkKeys.infinite(), context.previousData);
      }
    },

    // Refetch after mutation settles
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: bookmarkKeys.infinite() });
      queryClient.invalidateQueries({ queryKey: bookmarkKeys.lists() });
    },
  });
}

/**
 * Hook to restore a deleted bookmark (undo)
 */
export function useRestoreBookmark() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (bookmarkId: number) => api.restoreBookmark(bookmarkId),
    onSuccess: () => {
      // Invalidate to refetch updated list
      queryClient.invalidateQueries({ queryKey: bookmarkKeys.lists() });
      queryClient.invalidateQueries({ queryKey: bookmarkKeys.infinite() });
    },
  });
}
